import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { useRef, useState } from 'react';
import api from 'api';
import { Button, FlexContainer, ProductIdentifier } from 'components/common';
import { Box } from 'components/common/box';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import {
  DatepickerWithTime,
  Form,
  FormGroup,
  FormSection,
  RichText,
  Text
} from 'components/common/form';
import { UnsavedFormChanges } from 'components/common/notifications';
import {
  CalendarEventCategories,
  CalendarEventImage,
  CalendarEventSettingsChangedInfo
} from '.';

import type { FormApi } from 'informed';
import type { CalendarEventExtended } from 'shared-types';

export type EventSettingsValues = Pick<
  CalendarEventExtended,
  | 'categories'
  | 'customAttributes'
  | 'description'
  | 'imageUrl'
  | 'link'
  | 'linkText'
  | 'location'
  | 'title'
> & { [key in 'endDate' | 'lastJoinDate' | 'startDate']: number } & {
  recipientsType?: 'all' | 'none';
};

export type EventChangedValues = Pick<
  EventSettingsValues,
  | 'description'
  | 'endDate'
  | 'lastJoinDate'
  | 'link'
  | 'location'
  | 'startDate'
  | 'title'
>;

const CalendarEventSettings = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: { calendarEventCustomAttributes }
  } = useReduxState();

  const formApi = useRef<FormApi<EventSettingsValues>>(undefined);
  const uploader = useRef<any>(undefined);

  const [haveEmailValuesChanged, setHaveEmailValuesChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newImage, setNewImage] = useState(false);
  const [startDate, setStartDate] = useState<number>(
    Date.parse(calendarEvent.startDate)
  );

  if (!calendarEvent) return null;

  const handleFormChange = ({
    description,
    endDate,
    lastJoinDate,
    link,
    linkText,
    location,
    startDate,
    title
  }: EventSettingsValues) => {
    // email modal is only displayed if any of these values were changed
    // Note: loose equality is used on purpose because we treat
    // null and undefined as the same
    const hasChanged =
      description != initialValues.description ||
      endDate != initialValues.endDate ||
      lastJoinDate != initialValues.lastJoinDate ||
      link != initialValues.link ||
      linkText != initialValues.linkText ||
      location != initialValues.location ||
      startDate != initialValues.startDate ||
      title != initialValues.title;

    setHaveEmailValuesChanged(hasChanged);

    if (endDate < startDate) {
      formApi.current.setError('endDate', messages.eventCalendarDatesError);
    } else formApi.current.setError('endDate', undefined);
  };

  const handleStartDateChange = (value: number) => {
    const timeDiff = value - startDate;

    const endDate: number = formApi.current.getValue('endDate');
    formApi.current.setValue('endDate', endDate + timeDiff);

    const lastJoinDate = formApi.current.getValue('lastJoinDate') as number;
    if (lastJoinDate > value) formApi.current.setValue('lastJoinDate', value);

    setStartDate(value);
  };

  const handleSubmit = async (values: EventSettingsValues) => {
    try {
      setLoading(true);

      const sendEventChangedEmail =
        values.recipientsType === 'all' ? true : false;

      const data = { ...values, sendEventChangedEmail };

      if (newImage) {
        const response = await uploader.current.upload();
        if (response) data.imageUrl = response.url;
      }

      const startDate = new Date(data.startDate).toISOString();
      const endDate = new Date(data.endDate).toISOString();
      const lastJoinDate = data.lastJoinDate
        ? new Date(data.lastJoinDate).toISOString()
        : null;

      await api.put(`/admin/calendar-events/${calendarEvent.id}`, {
        ...data,
        startDate,
        endDate,
        lastJoinDate
      });

      dispatch.updatePageState({
        calendarEvent: {
          ...calendarEvent,
          ...data,
          startDate,
          endDate,
          lastJoinDate
        },
        hasUnsavedChanges: false,
        refreshInitialState: true
      });
      dispatch.addNotice({
        text: messages.settingsSavedSuccessfully,
        type: 'success'
      });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangedInfoSubmit = async (values: EventChangedValues) => {
    const mainFormValues = formApi.current.getState().values;
    await handleSubmit({ ...mainFormValues, ...values });
  };

  const validateTitle = (value: string) => {
    return !value?.trim() ? messages.commonTitleEmpty : undefined;
  };

  const initialValues: EventSettingsValues = {
    categories: calendarEvent.categories,
    customAttributes: calendarEvent.customAttributes,
    description: calendarEvent.description || '',
    endDate:
      typeof calendarEvent.endDate === 'number'
        ? calendarEvent.endDate
        : Date.parse(calendarEvent.endDate),
    imageUrl: calendarEvent.imageUrl,
    lastJoinDate: calendarEvent.lastJoinDate
      ? typeof calendarEvent.lastJoinDate === 'number'
        ? calendarEvent.lastJoinDate
        : Date.parse(calendarEvent.lastJoinDate)
      : null,
    link: calendarEvent.link,
    linkText: calendarEvent.linkText,
    location: calendarEvent.location,
    startDate:
      typeof calendarEvent.startDate === 'number'
        ? calendarEvent.startDate
        : Date.parse(calendarEvent.startDate),
    title: calendarEvent.title
  };

  const showChangedInfoModal =
    haveEmailValuesChanged &&
    calendarEvent.numberOfParticipantsAdded > 0 &&
    new Date(calendarEvent.endDate).getTime() > dateNow;

  return (
    <Box padding={0} title={messages.commonSettings}>
      <Form
        allowEmptyStrings={true}
        getApi={(form: FormApi<EventSettingsValues>) =>
          (formApi.current = form)
        }
        initialValues={initialValues}
        onValueChange={handleFormChange}
      >
        {({ formState }) => (
          <>
            <div style={{ padding: 15 }}>
              <UnsavedFormChanges initialValues={initialValues} />

              <FormGroup label={messages.commonTitle}>
                <Text
                  field="title"
                  id="title"
                  required={true}
                  validate={validateTitle}
                  validateOnChange={true}
                />
              </FormGroup>

              <ProductIdentifier
                productIdentifier={calendarEvent.productIdentifier}
              />

              <FlexContainer
                style={{
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '0 50px'
                }}
              >
                <FormGroup
                  label={messages.publishStartDate}
                  labelTooltip={messages.eventCalendarStartDateInfo}
                  maxWidth="280px"
                  style={{ marginTop: 15 }}
                >
                  <DatepickerWithTime
                    field="startDate"
                    id="startDate"
                    initialValue={Date.parse(calendarEvent.startDate)}
                    isClearable={false}
                    onChange={handleStartDateChange}
                  />
                </FormGroup>

                <FormGroup
                  label={messages.publishEndDate}
                  maxWidth="280px"
                  style={{ marginTop: 15 }}
                >
                  <DatepickerWithTime
                    field="endDate"
                    id="endDate"
                    initialValue={Date.parse(calendarEvent.endDate)}
                    isClearable={false}
                    minDate={formState.values.startDate}
                  />
                </FormGroup>
              </FlexContainer>

              <FlexContainer>
                <FormGroup
                  label={messages.eventCalendarJoinLastDate}
                  labelTooltip={messages.eventCalendarJoinLastDateInfo}
                  maxWidth="280px"
                  style={{ marginTop: 15 }}
                >
                  <DatepickerWithTime
                    field="lastJoinDate"
                    id="lastJoinDate"
                    initialValue={
                      calendarEvent.lastJoinDate
                        ? Date.parse(calendarEvent.lastJoinDate)
                        : null
                    }
                    isClearable={true}
                    maxDate={formState.values.startDate}
                    placeholder={messages.commonSetDate}
                  />
                </FormGroup>
              </FlexContainer>

              <FormGroup
                label={messages.commonLocation}
                labelHelpText={messages.eventCalendarLocationExamples}
              >
                <Text
                  field="location"
                  id="location"
                  placeholder={messages.commonLocationPlaceholder}
                />
              </FormGroup>

              <FormGroup label={messages.commonLink}>
                <Text
                  field="link"
                  id="link"
                  maxLength={2000}
                  placeholder={messages.commonAddLink}
                />
              </FormGroup>

              <FormGroup
                label={messages.uploaderLinkText}
                style={{ marginTop: 0 }}
              >
                <Text
                  field="linkText"
                  id="linkText"
                  placeholder={messages.commonAddLinkText}
                />
              </FormGroup>

              <FormGroup
                label={messages.commonDescription}
                style={{ marginTop: 25 }}
              >
                <RichText
                  autoGrow={false}
                  id="description"
                  field="description"
                  maxLength={3000}
                  placeholder={messages.commonDescriptionPlaceholder}
                />
              </FormGroup>

              <CalendarEventCategories />
            </div>

            <CalendarEventImage
              id="imageUrl"
              field="imageUrl"
              onFileAdded={() => setNewImage(true)}
              onFileRemoved={() => setNewImage(false)}
              setUploader={(fileUploader) => (uploader.current = fileUploader)}
            />

            {calendarEventCustomAttributes?.length ? (
              <FormSection
                maxWidth="100%"
                title={messages.settingsAdditionalSettings}
              >
                <div className="custom-attribute-settings">
                  <ProductCustomAttributes
                    attributes={calendarEvent.customAttributes || {}}
                    references={{
                      workload: calendarEvent.workloadValue
                    }}
                    type="calendarEvent"
                  />
                </div>
              </FormSection>
            ) : null}

            <FormSection>
              <FormGroup>
                {showChangedInfoModal ? (
                  <CalendarEventSettingsChangedInfo
                    onSubmit={handleChangedInfoSubmit}
                  />
                ) : (
                  <Button
                    category="success"
                    disabled={formState.invalid}
                    loading={loading}
                    onClick={() => handleSubmit(formState.values)}
                    text={messages.commonSaveChanges}
                  />
                )}
              </FormGroup>
            </FormSection>
          </>
        )}
      </Form>
    </Box>
  );
};

export default CalendarEventSettings;
